import { Card } from "./ui/card";
import { Skeleton } from "./ui/skeleton";
import { Alert } from "./ui/alert";
import { cn } from "@/lib/utils";

import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';


type Stats = {
  species: number;
  organisations: number;
  records: number;
  users: number;
}

export function StatsSection({ className }: { className?: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["stats"],
    queryFn: async () => {
      const res = await fetch("/api/stats");
      if (!res.ok) throw new Error("Failed to fetch stats");
      const json = await res.json() as Stats;
      return json;
    },
    staleTime: 1000 * 60 * 5,
  });
  if (isLoading) {
    return (
      <section className={cn("py-12 bg-muted/50 rounded-xl p-8", className)}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-12 w-24 mx-auto" />
              <Skeleton className="h-4 w-32 mx-auto" />
            </div>
          ))}
        </div>
      </section>
    );
  }
  if (error) {
    return (
      <section className={cn("py-12 bg-muted/50 rounded-xl p-8", className)}>
        <Alert variant="destructive">{error.message}</Alert>
      </section>
    );
  }
  if (!data) return null;
  return (
    <section className={cn("py-12 bg-muted/50 rounded-xl p-8", className)}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
        <div className="space-y-2">
          <h3 className="text-4xl font-bold text-primary">
            {data.species.toLocaleString()}
          </h3>
          <p className="text-muted-foreground">Species</p>
        </div>
        <div className="space-y-2">
          <h3 className="text-4xl font-bold text-primary">
            {data.organisations.toLocaleString()}
          </h3>
          <p className="text-muted-foreground">Organisations</p>
        </div>
        <div className="space-y-2">
          <h3 className="text-4xl font-bold text-primary">
            {data.records.toLocaleString()}
          </h3>
          <p className="text-muted-foreground">Recorded Sightings</p>
        </div>
        <div className="space-y-2">
          <h3 className="text-4xl font-bold text-primary">
            {data.users.toLocaleString()}
          </h3>
          <p className="text-muted-foreground">Users</p>
        </div>
      </div>
    </section>
  );
}

